import { Segment, Menu, Grid } from "semantic-ui-react";
import AdminRolesGrid from "./AdminRolesGrid";
import { AdminRole } from "../models/AdminRole";
import { AsyncActionStatus } from "../models/AsyncActionStatus";
import { useEffect, useState } from "react";
import { toastSuccess } from "../utils";
import EditAdminRoleModal from "./EditAdminRoleModal";
import { ApplicationPermission } from "../models/ApplicationPermission";

interface Props {
  isAdminRolesLoading: boolean;
  adminRoles?: AdminRole[];
  adminPermissions?: ApplicationPermission[];
  onAddAdminRole: (name: string, permissionNames: string[]) => void;
  addAdminRoleStatus: AsyncActionStatus;
  onEditAdminRole: (roleId: string, name: string, permissionNames: string[]) => void;
  editAdminRoleStatus: AsyncActionStatus;
}

const AdminRolesList = ({
  isAdminRolesLoading,
  adminRoles,
  adminPermissions,
  onAddAdminRole,
  addAdminRoleStatus,
  onEditAdminRole,
  editAdminRoleStatus,
}: Props) => {
  const [openEditAdminRoleModal, setOpenEditAdminRoleModal] = useState(false);
  const [selectedAdminRoleToEdit, setSelectedAdminRoleToEdit] = useState<AdminRole | undefined>(undefined);

  const handleEditAdminRole = (adminRoleId: string) => {
    const adminRole = adminRoles?.find((o) => o.id === adminRoleId);
    if (adminRole) {
      setSelectedAdminRoleToEdit({ ...adminRole } as AdminRole);
      setOpenEditAdminRoleModal(true);
    }
  };

  const handleModalClose = () => {
    setOpenEditAdminRoleModal(false);
    setSelectedAdminRoleToEdit(undefined);

    addAdminRoleStatus.reset();
    editAdminRoleStatus.reset();
  };

  const handleEditAdminRoleModalSubmit = (name: string, permissionNames: string[], roleId?: string) => {
    if (roleId) {
      onEditAdminRole(roleId, name, permissionNames);
    } else {
      onAddAdminRole(name, permissionNames);
    }
  };

  useEffect(() => {
    if (addAdminRoleStatus.successful) {
      toastSuccess("New role added successfully!");
      setOpenEditAdminRoleModal(false);
      setSelectedAdminRoleToEdit(undefined);
    }
  }, [addAdminRoleStatus.successful]);

  useEffect(() => {
    if (editAdminRoleStatus.successful) {
      toastSuccess("Role updated successfully!");
      setOpenEditAdminRoleModal(false);
      setSelectedAdminRoleToEdit(undefined);
    }
  }, [editAdminRoleStatus.successful]);

  return (
    <>
      <Menu secondary>
        <Menu.Item icon="plus" name="New role" onClick={(e) => setOpenEditAdminRoleModal(true)} />
      </Menu>

      <Segment basic loading={isAdminRolesLoading}>
        <Grid columns="1">
          <Grid.Row>
            <Grid.Column width="16">
              <AdminRolesGrid adminRoles={adminRoles} onEditAdminRole={handleEditAdminRole} />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Segment>

      <EditAdminRoleModal
        open={openEditAdminRoleModal}
        onClose={handleModalClose}
        onSubmit={handleEditAdminRoleModalSubmit}
        submitInProgress={addAdminRoleStatus.loading || editAdminRoleStatus.loading}
        adminRole={selectedAdminRoleToEdit}
        adminPermissions={adminPermissions}
        submitErrors={
          addAdminRoleStatus.errors
            ? addAdminRoleStatus.errors
            : editAdminRoleStatus.errors
            ? editAdminRoleStatus.errors
            : undefined
        }
      ></EditAdminRoleModal>
    </>
  );
};

export default AdminRolesList;
